import { useCallback, useMemo } from 'react';

import { parseEnum } from './parse';
import { HistoryMode, useUrlPatch } from './useUrlPatch';

interface UrlParamOptions<T extends string> {
  /** Allow-list; raw values outside it fall back to the default. */
  allowed?: readonly T[];
  /** Also drop the `page` param on change (filter/tab/search semantics). */
  resetPage?: boolean;
  /**
   * Extra param keys to delete on change, in the SAME write. Used by section
   * controls (tabs/view) to drop the params owned only by the section being
   * left, so they don't linger in the URL and bleed into the next section.
   */
  resetParams?: readonly string[];
  /** History mode for writes. Default 'replace'; tabs typically pass 'push'. */
  history?: HistoryMode;
}

/**
 * Single string param, source of truth = the URL. Validated against an
 * optional allow-list; invalid/absent values read back as `fallback`. Writing
 * the fallback deletes the param (clean URL).
 *
 * The read is memoized on the raw param string, so the returned value keeps a
 * stable identity across renders until the param actually changes — callers'
 * existing useCallback/useEffect deps stay correct and don't loop.
 */
export function useUrlParam<T extends string>(
  key: string,
  fallback: T,
  options: UrlParamOptions<T> = {}
): [T, (value: T) => void] {
  const { allowed, resetPage = false, resetParams, history = 'replace' } =
    options;
  const { searchParams, patch } = useUrlPatch();

  const raw = searchParams.get(key);
  const value = useMemo(
    () => parseEnum(raw, fallback, allowed),
    [raw, fallback, allowed]
  );

  const setValue = useCallback(
    (next: T) => {
      patch((params) => {
        if (!next || next === fallback) params.delete(key);
        else params.set(key, next);
        if (resetPage) params.delete('page');
        resetParams?.forEach((k) => params.delete(k));
      }, history);
    },
    [patch, key, fallback, resetPage, resetParams, history]
  );

  return [value, setValue];
}
